import React, { memo } from 'react'
import { Trans } from 'react-i18next'

import styled from '@emotion/styled'

import { useEventCapture } from '@surglogs-app/analytics'
import {
  MODULE_POLICIES,
  PermissionAction,
  SETTINGS_PERMISSION_MODULE,
  SettingsResourceTypes,
  useRolesAuthPermissions,
} from '@surglogs-app/auth'
import { useHandler } from '@surglogs-app/hooks'
import { Cross, StatusActive } from '@surglogs-app/icons'
import { useModal } from '@surglogs-app/prompt'
import { Config } from '@surglogs-app/types'
import {
  Title,
  Box,
  FlexBox,
  TextBold,
  Text,
  Button,
  absoluteVerticalCenter,
  Icon,
  Pill,
} from '@surglogs-app/ui'
import { useToast } from '@surglogs-app/ui.toast'

import { PromoModalContext } from '../constants/Analytics'
import { FEATURES } from '../constants/Features'
import { useTrialRequestSubmitHandler } from '../hooks/useTrialRequestSubmitHandler'
import { PromoAuthData } from '../models'
import { useTranslation } from '../services'
import { PromoModalFeature } from '../types'

import { PromoModalFeaturesGroup } from './PromoModalFeaturesGroup'
import { PromoModalForm, PromoModalFormValues } from './PromoModalForm'
import { PromoModalVideo } from './PromoModalVideo'

const HIDE_IMAGE_BREAKPOINT = '1050px'

const PromoModalContentContainer = styled(FlexBox)<{
  withHeader?: boolean
}>`
  flex: 1;
  column-gap: 90px;
  align-items: flex-start;
  position: relative;
  padding: ${({ withHeader }) => (withHeader ? '30px' : '75px')} 110px 65px 30px;

  @media ${({ theme }) => theme.mq.md} {
    padding: ${({ withHeader }) => (withHeader ? '30px' : '50px')} 45px 50px
      60px;
    column-gap: 50px;
  }
`

const HeaderContainer = styled(PromoModalContentContainer)`
  padding-top: 30px;
  padding-bottom: 30px;

  @media ${({ theme }) => theme.mq.md} {
    padding-top: 30px;
    padding-bottom: 30px;
  }
`

const CloseButton = styled(Button)`
  position: absolute !important;
  top: 7px;
  right: 10px;
  border: none;
  z-index: 2;
  background-color: inherit;

  &:hover {
    background-color: inherit;
  }
`

const LeftHalf = styled(FlexBox)`
  width: 50%;
  z-index: 1;
  justify-content: flex-end;

  @media (max-width: ${HIDE_IMAGE_BREAKPOINT}) {
    display: none;
  }
`

const StyledImage = styled.img<{ hasVideo: boolean }>`
  z-index: 2;
  max-width: 50%;
  position: absolute;
  ${absoluteVerticalCenter()};
  cursor: ${({ hasVideo }) => (hasVideo ? 'pointer' : 'default')};

  @media (max-width: ${HIDE_IMAGE_BREAKPOINT}) {
    display: none;
  }
`

const RightHalf = styled(Box)`
  flex-shrink: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  max-width: 435px;

  @media (max-width: ${HIDE_IMAGE_BREAKPOINT}) {
    max-width: 100%;
  }
`

const Background = styled(Box)<{ withHeader?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 70%;
  height: 100%;
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  border-radius: ${({ withHeader }) => (withHeader ? '0 0 30px 30px' : '30px')};
  z-index: 0;

  @media (max-width: ${HIDE_IMAGE_BREAKPOINT}) {
    width: 100%;
  }
`

const Wrapper = styled(Box)`
  position: relative;

  @media (max-width: ${HIDE_IMAGE_BREAKPOINT}) {
    margin-left: 80px;
  }
`

const MainBackground = styled(Background)`
  background-color: ${({ theme }) => theme.ui.colors.background};
`

interface PromoModalContentProps {
  onCancel: () => void
  onSubmitSuccess: (data: PromoModalFormValues) => void
  featureName: PromoModalFeature
  config: Config
  context: PromoModalContext
  authData?: PromoAuthData
  clinicId?: number
}

type PromoModalFeaturesWithHeader = typeof MODULE_POLICIES

export const PromoModalContent = memo(
  ({
    onCancel,
    onSubmitSuccess,
    featureName,
    config,
    context,
    authData,
    clinicId,
  }: PromoModalContentProps) => {
    const { t } = useTranslation()
    const { image, videoUrl, isInDevelopment, isNewFeature, withHeader } =
      FEATURES[featureName]
    const { captureEvent } = useEventCapture()
    const { showToast } = useToast()

    const { hasPermission: canManageFacility } = useRolesAuthPermissions(
      Number(clinicId),
      [
        {
          module: SETTINGS_PERMISSION_MODULE,
          resourceType: SettingsResourceTypes.FACILITY_RESOURCE,
          action: PermissionAction.MANAGE,
        },
      ],
      { defaultsTo: true, skip: !clinicId },
    )
    const onSuccess = useHandler((data: PromoModalFormValues) => {
      showToast({
        title: t('successMessage.title'),
        text: t('successMessage.text'),
      })
      onSubmitSuccess(data)
    })

    const { onSubmit, isPending, canCreateRequest } =
      useTrialRequestSubmitHandler({
        featureName,
        onSuccess,
        config,
        context,
        authData,
        canManageFacility,
      })

    const [showPromoVideo] = useModal(
      ({ hideModal }) => {
        if (!videoUrl) {
          return null
        }

        return (
          <PromoModalVideo {...{ hideModal, videoUrl, featureName, context }} />
        )
      },
      {
        autoClose: false,
      },
    )

    const onImageClick = useHandler(() => {
      showPromoVideo()
      captureEvent('promo_video_played', {
        type: featureName,
        context,
      })
    })

    const getHeadline = useHandler(() => {
      if (featureName.includes('SURVEY_READINESS')) {
        if (featureName.includes('SURVEY_READINESS_A')) {
          return 'features.SURVEY_READINESS_A.headline' as const
        }

        return 'features.SURVEY_READINESS_B.headline' as const
      }

      return `features.${featureName}.headline` as const
    })

    return (
      <Wrapper>
        <StyledImage
          {...{
            src: String(image),
            alt: 'module',
            hasVideo: !!videoUrl,
            onClick: videoUrl ? onImageClick : undefined,
          }}
        />
        <MainBackground />
        <CloseButton
          {...{
            shape: 'square',
            onClick: onCancel,
            variant: 'secondary',
            'aria-label': 'close-promo-modal',
          }}>
          <Icon {...{ icon: Cross, color: 'inherit' }} />
        </CloseButton>
        {withHeader ? (
          <HeaderContainer>
            <LeftHalf />
            <RightHalf>
              <FlexBox
                {...{
                  flex: '1',
                  fd: 'column',
                  gap: 'md',
                  ai: 'flex-start',
                }}>
                <Title {...{ size: 'md' }}>
                  <Trans
                    {...{
                      t,
                      i18nKey: `features.${
                        featureName as PromoModalFeaturesWithHeader
                      }.header.headline`,
                      components: {
                        highlight: (
                          <TextBold
                            {...{
                              variant: 'primary',
                              fz: 'inherit',
                              td: 'underline',
                            }}
                          />
                        ),
                      },
                    }}
                  />
                </Title>
                <Text {...{ size: 'xxs' }}>
                  <Trans
                    {...{
                      t,
                      i18nKey: `features.${
                        featureName as PromoModalFeaturesWithHeader
                      }.header.content`,
                      components: { b: <TextBold {...{ fz: 'inherit' }} /> },
                    }}
                  />
                </Text>
              </FlexBox>
            </RightHalf>
          </HeaderContainer>
        ) : null}
        <PromoModalContentContainer {...{ withHeader }}>
          <Background {...{ withHeader }} />
          <LeftHalf />
          <RightHalf>
            <Title {...{ size: 'md', maw: '435px', mb: 'lg' }}>
              {isNewFeature ? (
                <Pill
                  {...{
                    color: 'green.100',
                    variant: 'light',
                    isRounded: true,
                    mr: 'sm',
                    display: 'inline-flex',
                    ai: 'center',
                  }}>
                  <Icon
                    {...{
                      icon: StatusActive,
                      size: 'abs.12',
                      mr: 'xs',
                      color: 'green.100',
                      fsh: 0,
                    }}
                  />
                  {t('newFeature')}
                </Pill>
              ) : null}
              <Trans
                {...{
                  t,
                  i18nKey: getHeadline(),
                  components: {
                    highlight: (
                      <TextBold
                        {...{
                          variant: 'primary',
                          fz: 'inherit',
                        }}
                      />
                    ),
                  },
                }}
              />
            </Title>
            <PromoModalFeaturesGroup {...{ featureName }} />
            <Text {...{ size: 'xxs', mt: 'lg', mb: 'md' }}>
              <Trans
                {...{
                  t,
                  i18nKey: isInDevelopment ? 'confirmInDevelopment' : 'confirm',
                  components: { b: <TextBold {...{ fz: 'inherit' }} /> },
                }}
              />
            </Text>
            <PromoModalForm
              {...{ onSubmit, disabled: isPending || !canCreateRequest }}
            />
          </RightHalf>
        </PromoModalContentContainer>
      </Wrapper>
    )
  },
)
